import { useState } from 'react';
import { useAction, useQuery } from 'convex/react';
import { CalendarX, Loader2 } from 'lucide-react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { Button } from '@bill-alps/ui';
import { useTranslation } from '../i18n/useTranslation';
import { formatDate } from '@/lib/format/date';

/**
 * "Your subscription is cancelled — access until {date}" bar.
 *
 * Mounted inside `<GlobalBanners>`, which owns the sticky positioning and
 * measures the stack into `--global-banners-height` — this component renders
 * only the bar (see `sandbox-banner.tsx`).
 *
 * That placement is the point. `GlobalBanners` sits above every shell,
 * including onboarding, which is where a user lands after deleting their
 * company — the one screen where the account used to show no trace whatsoever
 * of a cancellation that had just gone through. The `deletedCompany` variant is
 * read from a tombstone precisely because no company row survives to carry it.
 *
 * Not dismissible: a cancellation the user cannot find is the defect this
 * fixes.
 */
export function SubscriptionBanner() {
  const { t } = useTranslation('subscriptionBanner');
  const notice = useQuery(
    api.features.stripe.subscriptions.getCancellationNotice,
  );
  const createPortal = useAction(
    api.features.stripe.subscriptions.createCustomerPortalSession,
  );
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!notice) return null;

  const handleReactivate = async () => {
    if (!notice.companyId) return;
    setLoading(true);
    setFailed(false);
    try {
      const { url } = await createPortal({ companyId: notice.companyId });
      if (url) window.location.href = url;
      else setLoading(false);
    } catch {
      // Inline rather than a toast: the bar also renders on the auth-style
      // screens, and `<Toaster />` only exists inside the app shell — a toast
      // there would fail silently, on the one action this bar offers.
      setFailed(true);
      setLoading(false);
    }
  };

  const date = formatDate(notice.endsAt);

  return (
    <div
      role="status"
      data-testid="subscription-cancellation-banner"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b-2 border-border bg-riso-amber/15 px-4 py-2 text-sm"
    >
      <span className="flex items-center gap-2 font-medium text-foreground">
        <CalendarX className="h-4 w-4 shrink-0" />
        {notice.source === 'company'
          ? t('canceledKeepsAccess', { date })
          : t('canceledAfterDeletion', { date })}
      </span>
      {notice.canReactivate && (
        <Button
          variant="accent"
          size="sm"
          onClick={() => void handleReactivate()}
          disabled={loading}
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('reactivate')}
        </Button>
      )}
      {failed && (
        <span role="alert" className="text-destructive">
          {t('reactivateError')}
        </span>
      )}
    </div>
  );
}
